export const BLOCK_MARKER_REGEX = /<<BLOCK:([^>]+)>>/g;

const CODE_SPAN_REGEX = /(`+)[\s\S]*?\1/g;

export type BlockMarkerContext = {
  name: string;
  standalone: boolean;
  blankLineBefore: boolean;
  blankLineAfter: boolean;
};

const isBlankLine = (source: string, start: number, end: number): boolean =>
  source.slice(start, end).trim() === '';

const lineEndFrom = (source: string, lineStart: number): number => {
  const newlineAfter = source.indexOf('\n', lineStart);
  return newlineAfter === -1 ? source.length : newlineAfter;
};

const getLineContext = (source: string, start: number, end: number) => {
  const lineStart = source.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = lineEndFrom(source, end);

  const standalone =
    isBlankLine(source, lineStart, start) && isBlankLine(source, end, lineEnd);

  const previousLineStart = source.lastIndexOf('\n', lineStart - 2) + 1;
  const blankLineBefore =
    lineStart === 0 || isBlankLine(source, previousLineStart, lineStart - 1);

  const nextLineStart = lineEnd + 1;
  const blankLineAfter =
    lineEnd === source.length ||
    isBlankLine(source, nextLineStart, lineEndFrom(source, nextLineStart));

  return { standalone, blankLineBefore, blankLineAfter };
};

export const padBlockForExport = (
  text: string,
  {
    blankLineBefore,
    blankLineAfter,
  }: Pick<BlockMarkerContext, 'blankLineBefore' | 'blankLineAfter'>,
): string =>
  (blankLineBefore ? '' : '\n') + text + (blankLineAfter ? '' : '\n');

export const replaceBlockMarkers = (
  markdown: string,
  replace: (context: BlockMarkerContext) => string,
): string => {
  const convertSegment = (segment: string, offset: number): string =>
    segment.replace(
      BLOCK_MARKER_REGEX,
      (match: string, name: string, index: number) => {
        const lineContext = getLineContext(
          markdown,
          offset + index,
          offset + index + match.length,
        );
        return replace({ name: name.trim(), ...lineContext });
      },
    );

  let result = '';
  let lastIndex = 0;
  CODE_SPAN_REGEX.lastIndex = 0;
  for (const match of markdown.matchAll(CODE_SPAN_REGEX)) {
    const matchIndex = match.index ?? 0;
    result += convertSegment(markdown.slice(lastIndex, matchIndex), lastIndex);
    result += match[0];
    lastIndex = matchIndex + match[0].length;
  }
  result += convertSegment(markdown.slice(lastIndex), lastIndex);
  return result;
};
